/**
 * A layout in the hall, being edited: opened from the server (or the device, offline), saved with a
 * click or Ctrl+S, refused when someone saved since (and the lead chooses what to do), with its
 * history and its view-only links a click away. Unsaved changes found on the device are offered back.
 */

import type { EventRow, LayoutRow } from '@mawkeb/shared/api';
import type { Layout } from '@mawkeb/shared/layout';
import type { HallResponse } from '@mawkeb/shared/manifest';
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, fetchHall } from '../../api';
import { navigate } from '../../router';
import { cacheGet } from '../../state/cache';
import { layoutState } from '../../state/layout';
import { draftFor, dropDraft, layoutKey, saving, useSaving, useUnsaved, type CachedLayout, type Draft } from '../../state/saving';
import { useVenue, venue } from '../../state/store';
import { Dialog } from '../Dialog';
import { HallScreen } from '../HallScreen';
import { HistoryPanel } from '../History';
import { Back, Offline, Saved, Share, Versions } from '../icons';
import { ShareDialog } from '../Share';
import { ago } from '../time';
import { Link } from './Shell';

function SaveState() {
  const unsaved = useUnsaved();
  const status = useSaving((s) => s.status);
  const problem = useSaving((s) => s.problem);
  const row = useSaving((s) => s.row);
  const looking = useSaving((s) => s.looking);
  if (!row) return null;
  if (looking) return <span className="save-state">Looking at version {looking.version}</span>;
  let text: string;
  let tone = '';
  if (status === 'saving') text = 'Saving…';
  else if (status === 'conflict') {
    text = 'Not saved';
    tone = 'bad';
  } else if (status === 'waiting' || problem) {
    text = 'Not saved yet';
    tone = 'warn';
  } else if (unsaved) text = 'Unsaved changes';
  else text = 'Saved';
  const title = problem ?? (unsaved ? 'Save (Ctrl+S) to keep these changes for everyone.' : `Version ${row.version}, saved by ${row.savedBy.name} ${ago(row.savedAt)}`);
  return (
    <div className="save-wrap">
      <span className={`save-state ${tone}`} role="status" title={title}>
        {!unsaved && status === 'idle' && !problem && <Saved />}
        {status === 'waiting' && problem?.startsWith('No connection') && <Offline />}
        <span>{text}</span>
      </span>
      {(unsaved || status === 'waiting') && status !== 'conflict' && (
        <button type="button" className="primary small" onClick={() => void saving().save()} disabled={status === 'saving'} title={problem ?? 'Save (Ctrl+S)'}>
          {status === 'waiting' ? 'Try now' : 'Save'}
        </button>
      )}
    </div>
  );
}

/** Says the hall is the synthetic stand-in until the real scan replaces it (on a laptop; a phone's Settings says it). */
export function StandInTag() {
  const manifest = useVenue((s) => s.manifest);
  if (!manifest?.standIn) return null;
  return (
    <span className="tag quiet only-laptop" title={manifest.label}>
      Stand-in scan
    </span>
  );
}

function Head() {
  const row = useSaving((s) => s.row);
  const event = useSaving((s) => s.event);
  const online = useVenue((s) => s.online);
  if (!row) return null;
  return (
    <div className="where editor-head">
      <Link to={`/e/${row.eventId}`} className="icon back" aria-label={`Back to ${event?.name ?? 'the event'}`} title={`Back to ${event?.name ?? 'the event'}`}>
        <Back />
      </Link>
      <div className="names">
        <span className="layout-name" dir="auto">
          {row.name}
        </span>
        <span className="event-name" dir="auto">
          {event?.name ?? ''}
        </span>
      </div>
      <StandInTag />
      {!online && (
        <span className="tag quiet" title="No connection. Changes are kept on this device and saved when you’re back online.">
          <Offline /> Offline
        </span>
      )}
      <SaveState />
    </div>
  );
}

/** The save was refused: someone saved since. Nothing was overwritten; the lead picks what happens to their changes. */
function Refused({ onReload }: { onReload: () => void }) {
  const conflict = useSaving((s) => s.conflict);
  const row = useSaving((s) => s.row);
  const [busy, setBusy] = useState(false);
  if (!conflict || !row) return null;
  const keepMine = async () => {
    setBusy(true);
    try {
      const r = await api.makeLayout(row.eventId, { name: `${row.name} (your changes)`.slice(0, 120), data: layoutState().layout });
      await dropDraft(row.id);
      saving().settled();
      saving().close();
      navigate(`/l/${r.layout.id}`, { force: true });
    } catch (e) {
      venue().toast((e as Error).message, { tone: 'bad' });
      setBusy(false);
    }
  };
  const theirs = async () => {
    if (!confirm('Open their version? Your changes will be discarded.')) return;
    await dropDraft(row.id);
    saving().settled();
    onReload();
  };
  return (
    <Dialog
      title="Someone saved this layout since you opened it"
      onClose={() => {}}
      closable={false}
      footer={
        <>
          <button type="button" className="secondary" onClick={() => void theirs()} disabled={busy}>
            Open their version
          </button>
          <button type="button" className="primary" onClick={() => void keepMine()} disabled={busy}>
            Keep mine as a new layout
          </button>
        </>
      }
    >
      <p className="lead-text">
        {conflict.current.savedBy.name} saved version {conflict.current.version} {ago(conflict.current.savedAt)}. Your changes weren’t saved, and nothing of theirs was overwritten.
      </p>
      <p className="form-note">Keep yours as a new layout in this event, then compare the two; or open their version and leave yours.</p>
    </Dialog>
  );
}

interface Offer {
  draft: Draft;
  row: LayoutRow;
  data: Layout;
  event: EventRow | null;
  /** Someone saved since the changes were made. */
  stale: boolean;
}

function DraftOffer({ offer, onKeep, onDiscard, onKeepAsNew }: { offer: Offer; onKeep: () => void; onDiscard: () => void; onKeepAsNew: () => void }) {
  const when = ago(offer.draft.at);
  return (
    <Dialog
      title="You have unsaved changes"
      onClose={onDiscard}
      closable={false}
      footer={
        offer.stale ? (
          <>
            <button type="button" className="secondary" onClick={onDiscard}>
              Discard mine
            </button>
            <button type="button" className="primary" onClick={onKeepAsNew}>
              Keep mine as a new layout
            </button>
          </>
        ) : (
          <>
            <button type="button" className="secondary" onClick={onDiscard}>
              Discard them
            </button>
            <button type="button" className="primary" onClick={onKeep}>
              Keep them
            </button>
          </>
        )
      }
    >
      {offer.stale ? (
        <p className="lead-text">
          This device kept changes to “{offer.row.name}” from {when}, but {offer.row.savedBy.name} has saved it since, so yours can’t simply be saved over it.
        </p>
      ) : (
        <p className="lead-text">This device kept changes to “{offer.row.name}” from {when} that weren’t saved. Keep them to carry on where you left off, then save.</p>
      )}
    </Dialog>
  );
}

export function EditorPage({ id }: { id: string }) {
  const [hall, setHall] = useState<HallResponse | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [panel, setPanel] = useState<'history' | 'share' | null>(null);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const row = useSaving((s) => s.row);
  const looking = useSaving((s) => s.looking);
  const set = useVenue((s) => s.set);

  const open = useCallback(
    async (fresh = false) => {
      setFailed(null);
      let got: { layout: LayoutRow; data: Layout; event: EventRow | null } | null = null;
      try {
        got = await api.layout(id);
      } catch (e) {
        const err = e as ApiError;
        if (err.status === 401) return;
        const cached = err.status === 'offline' ? await cacheGet<CachedLayout>(layoutKey(id)) : undefined;
        if (cached) {
          got = cached;
          venue().toast('No connection: this is the layout as this device last saw it. Changes are kept on the device until you’re back online.');
        } else {
          setFailed(err.status === 'offline' ? 'There’s no connection, and this layout hasn’t been opened on this device before.' : err.message);
          return;
        }
      }
      const draft = fresh ? undefined : await draftFor(id);
      if (draft && draft.base <= got.layout.version) {
        setOffer({ draft, row: got.layout, data: got.data, event: got.event, stale: draft.base < got.layout.version });
        return;
      }
      if (draft) await dropDraft(id);
      saving().open(got.layout, got.data, got.event);
    },
    [id],
  );

  useEffect(() => {
    set({ editable: true });
    void open();
    return () => saving().close();
  }, [open, set]);

  useEffect(() => {
    if (!row) return;
    let cancelled = false;
    venue().setLoad({ phase: 'manifest', message: undefined });
    fetchHall(row.hall)
      .then((r) => {
        if (cancelled) return;
        set({ manifest: r.manifest, model: r.manifest.model });
        venue().mark('manifest');
        setHall(r);
        if (r.offline) venue().toast('No connection: this is the hall as it was last opened on this device.');
      })
      .catch((e: Error) => !cancelled && venue().setLoad({ phase: 'error', message: e.message }));
    return () => {
      cancelled = true;
    };
  }, [row?.hall, attempt, set]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // an old version on show can't be edited
    set({ editable: !looking });
  }, [looking, set]);

  const reload = useCallback(() => {
    saving().close();
    void open(true);
  }, [open]);

  if (failed) {
    return (
      <div className="message">
        <div className="panel card">
          <h1>That layout couldn’t be opened</h1>
          <p>{failed}</p>
          <button type="button" className="primary" onClick={() => navigate('/')}>
            Back to the events
          </button>
        </div>
      </div>
    );
  }

  if (offer) {
    return (
      <DraftOffer
        offer={offer}
        onKeep={() => {
          setOffer(null);
          saving().open(offer.row, offer.data, offer.event, offer.draft.data);
        }}
        onDiscard={() => {
          setOffer(null);
          void dropDraft(id);
          saving().open(offer.row, offer.data, offer.event);
        }}
        onKeepAsNew={() => {
          setOffer(null);
          void api
            .makeLayout(offer.row.eventId, { name: `${offer.row.name} (your changes)`.slice(0, 120), data: offer.draft.data })
            .then(async (r) => {
              await dropDraft(id);
              navigate(`/l/${r.layout.id}`, { force: true });
            })
            .catch((e: Error) => {
              venue().toast(e.message, { tone: 'bad' });
              saving().open(offer.row, offer.data, offer.event);
            });
        }}
      />
    );
  }

  const actions = (
    <>
      <button type="button" className="icon" aria-label="History" title="History: every saved version" aria-pressed={panel === 'history'} onClick={() => setPanel(panel === 'history' ? null : 'history')}>
        <Versions />
      </button>
      <button type="button" className="icon" aria-label="Share a view-only link" title="Share a view-only link" onClick={() => setPanel('share')}>
        <Share />
      </button>
    </>
  );

  return (
    <HallScreen
      hall={hall}
      mode="edit"
      onRetry={() => setAttempt((n) => n + 1)}
      head={<Head />}
      actions={actions}
      phoneActions={[
        { label: 'History', run: () => setPanel('history') },
        { label: 'Share a view-only link', run: () => setPanel('share') },
      ]}
    >
      {panel === 'history' && <HistoryPanel onClose={() => setPanel(null)} onRestored={reload} />}
      {panel === 'share' && row && <ShareDialog layout={row} onClose={() => setPanel(null)} onChanged={(r) => saving().setRow({ ...row, showReciters: r.showReciters })} />}
      <Refused onReload={reload} />
    </HallScreen>
  );
}
